import { Typography, Stack } from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import { infoIcon } from 'common/assets/images';
import { Loader } from 'common/components/Loader/Loader';
import InfoTooltip from 'common/components/Tooltip/Tooltip';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { ENTITY_TYPE } from 'common/constants/TaxonomyConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { TOOLTIP_TEXT } from 'common/constants/Tooltip';
import {
	setToastMessage,
	setApprovalDrawerTitle,
} from 'common/store/reducer/commonReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import {
	getEntityRelationMapping,
	getControlData,
	getProcessData,
	getRiskData,
} from 'features/RiskProfile/Taxonomy/api';
import { getLevelInfo } from 'features/RiskProfile/Taxonomy/EntityLandingPage/EntityLandingUtils';
import HierarchyCard from 'features/RiskProfile/Taxonomy/EntityLandingPage/HierarchyCard';
import RelationMappingTable from 'features/RiskProfile/Taxonomy/RelationMapping/MappingDetail/Overview/RelationshipMappingTable/RelationshipMappingTable';

import { IOverviewProps } from '../MyApprovalUtils';

import styles from './MyApprovalOverviewTab.module.scss';

const { OK, FORBIDDEN } = HTTP_STATUS_CODES;
const { PROCESS, CONTROL, RISK } = ENTITY_TYPE;

const MyApprovalOverviewTab = (props: IOverviewProps) => {
	const { entityId, entityType } = props;
	const useCustomStyles = true;
	const dispatch = useDispatch();
	const [entityDetails, setEntityDetails] = useState<any>(null);
	const [levelList, setLevelList] = useState<string[]>([]);
	const [mappingTableData, setMappingTable] = useState<any>({ content: [] });
	const [loading, setLoading] = useState(true);

	const fetchProcessesDetails = async () => {
		setLoading(true);
		const response: any = await getProcessData(entityId);
		if (response.statusCode === OK) {
			const { responseBody } = response;
			if (responseBody) {
				setEntityDetails(responseBody);
				setLevelList(getLevelInfo(responseBody));
				dispatch(setApprovalDrawerTitle(responseBody?.processName));
			}
		}
		setLoading(false);
	};

	const fetchRisksDetails = async () => {
		setLoading(true);
		const response = await getRiskData(entityId);
		if (response?.statusCode === OK) {
			const { responseBody } = response;
			if (responseBody) {
				setEntityDetails(responseBody);
				setLevelList(getLevelInfo(responseBody));
				dispatch(setApprovalDrawerTitle(responseBody?.riskName || ''));
			}
		} else if (
			response?.statusCode !== FORBIDDEN &&
			response?.statusCode !== OK
		) {
			dispatch(
				setToastMessage({
					message: response?.errorBody?.errorMessage || APP_GENERAL_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		setLoading(false);
	};

	const fetchControlsDetails = async () => {
		setLoading(true);
		const response: any = await getControlData(entityId);
		if (response?.statusCode === OK) {
			const { responseBody } = response;
			if (responseBody) {
				setEntityDetails(responseBody);
				setLevelList(getLevelInfo(responseBody));
				dispatch(setApprovalDrawerTitle(responseBody?.name));
			}
		}
		setLoading(false);
	};

	const fetchEntityDetails = () => {
		switch (entityType?.toLowerCase()) {
			case CONTROL.toLowerCase():
				fetchControlsDetails();
				break;
			case RISK.toLowerCase():
				fetchRisksDetails();
				break;
			case PROCESS.toLowerCase():
				fetchProcessesDetails();
				break;
			default:
				fetchControlsDetails();
		}
	};

	useEffect(() => {
		if (entityType) {
			fetchEntityDetails();
		}
		fetchRelationMapping();
	}, [entityType]);

	const fetchRelationMapping = async (data?: any) => {
		const updatedHeaders = {
			relationId: entityId,
			relationType: entityType,
		};

		const params = {
			page: data?.pageNumber,
			size: data?.pageSize,
			filterType: 'all',
			sort: data?.sortDirection,
			sortKey: data?.sortKey,
		};
		const response = await getEntityRelationMapping(updatedHeaders, params);
		if (response?.statusCode === OK) {
			const { responseBody } = response;
			if (responseBody) {
				setMappingTable(responseBody);
			}
		}
	};

	const displayValue = (
		value: string | null | undefined,
		newValue: string | null | undefined,
		showInNextLine: boolean,
	) => {
		if (value || newValue) {
			return (
				<div
					className={
						showInNextLine ? styles['title-gap-next-line'] : styles['title-gap']
					}
				>
					<Typography
						className={newValue ? styles['cut-text'] : styles['normal-text']}
					>
						{value}
					</Typography>
					<Typography className={styles['normal-text']}>{newValue}</Typography>
				</div>
			);
		}
		return <Typography className={styles['na-text']}>N/A</Typography>;
	};

	return (
		<div className={styles['overview-main']}>
			{/* Loader */}
			{loading ? (
				<Loader show />
			) : (
				<>
					{/* Details */}
					<div className={styles['detail-container']}>
						<Typography
							data-testid="details-container"
							className={styles['detail-heading']}
						>
							Details
						</Typography>

						{entityType === PROCESS && (
							<Stack direction="row" className={styles['detail-child']}>
								<Typography className={styles['detail-title']}>
									Industry
								</Typography>
								<div
									data-testid="process-container"
									className={styles['detail-content']}
								>
									{displayValue(
										entityDetails?.industryType,
										entityDetails?.industryTypeNew,
										false,
									)}
								</div>
							</Stack>
						)}

						{entityType === RISK && (
							<Stack direction="row" className={styles['detail-child']}>
								<Typography className={styles['detail-title']}>
									Business Unit
								</Typography>
								<div className={styles['detail-content']}>
									{displayValue(
										entityDetails?.businessUnit,
										entityDetails?.businessUnitNew,
										false,
									)}
								</div>
							</Stack>
						)}

						{entityType === CONTROL && (
							<>
								<Stack direction="row" className={styles['detail-child']}>
									<Typography className={styles['detail-title']}>
										Frequency
									</Typography>
									<div className={styles['detail-content']}>
										{displayValue(
											entityDetails?.frequency,
											entityDetails?.frequencyNew,
											false,
										)}
									</div>
								</Stack>
								<Stack direction="row" className={styles['detail-child']}>
									<Typography className={styles['detail-title']}>
										Purpose
									</Typography>
									<div className={styles['detail-content']}>
										{displayValue(
											entityDetails?.purpose,
											entityDetails?.purposeNew,
											false,
										)}
									</div>
								</Stack>
							</>
						)}
					</div>

					{/* RISK Statement */}
					{entityType === RISK && (
						<div className={styles['detail-container']}>
							<Typography className={styles['detail-heading']}>
								Risk Statement
							</Typography>
							<Stack direction="row" spacing={3}>
								<div className={styles['risk-text']}>
									{displayValue(
										entityDetails?.riskStatement,
										entityDetails?.riskStatementNew,
										true,
									)}
								</div>
							</Stack>
						</div>
					)}

					{/* Control */}
					{entityType === CONTROL && (
						<>
							<div className={styles['detail-container']}>
								<Typography className={styles['detail-heading']}>
									Control Objective
								</Typography>
								<Stack direction="row" spacing={3}>
									<div className={styles['risk-text']}>
										{displayValue(
											entityDetails?.controlObjective,
											entityDetails?.controlObjectiveNew,
											true,
										)}
									</div>
								</Stack>
							</div>
							<div className={styles['detail-container']}>
								<Typography className={styles['detail-heading']}>
									Control Statement
								</Typography>
								<Stack direction="row" spacing={3}>
									<div className={styles['risk-text']}>
										{displayValue(
											entityDetails?.controlStatement,
											entityDetails?.controlStatementNew,
											true,
										)}
									</div>
								</Stack>
							</div>
							<div className={styles['detail-container']}>
								<Typography className={styles['detail-heading']}>
									Control Test Script
								</Typography>
								<Stack direction="row" spacing={3}>
									<div className={styles['risk-text']}>
										{displayValue(
											entityDetails?.controlTestScripts,
											entityDetails?.controlTestScriptsNew,
											true,
										)}
									</div>
								</Stack>
							</div>
						</>
					)}

					{/* HierarchyCard */}
					{(entityType === PROCESS || entityType === RISK) && (
						<div>
							<HierarchyCard
								coveredLists={levelList}
								customStyle={useCustomStyles}
							/>
						</div>
					)}

					{/* Relationship Mapping */}
					<div>
						<div className={styles['mapping-title']}>
							<Typography className={styles['detail-heading']}>
								{toSentenceCase('Relationship Mapping')}
							</Typography>
							<InfoTooltip
								className={styles['overview-info-tooltip']}
								title={TOOLTIP_TEXT.RELATIONSHIP_MAPPING}
							>
								<img src={infoIcon} alt="i" />
							</InfoTooltip>
						</div>
						{mappingTableData.content.length > 0 && (
							<RelationMappingTable
								relationId={entityId}
								relationType={entityType}
								tableDataValues={mappingTableData}
								fetchUpdatedTableValues={fetchRelationMapping}
							/>
						)}
					</div>
				</>
			)}
		</div>
	);
};

export default MyApprovalOverviewTab;
